import {
	Currency,
	SyncData,
	TableQueryFilter,
	ArchievedWithSyncModel,
	ZomentumEntities,
	ZomentumPSAErrorTypes
} from "../common";
import { ChildEntityHydrationParams, WithQueryFilter } from "../Utils";
import { UserData } from "../Users";
import { Activity } from "../Others/RecentActivityFeed";
import { Document } from "../Documents";
import { Client, ClientContact } from "../Clients";
import { OpportunityStage, Pipeline } from "../Pipelines";
import { CustomField } from "../Settings";
import { VendorCompany } from "../Marketplace/Vendor";
import { VendorClient } from "../Vendor/Clients";

export enum OpportunitySource {
	PARTNER = "partner",
	VENDOR = "vendor"
}

export enum OpportunityShareStatus {
	WAITING = "waiting",
	ACCEPTED = "accepted",
	REJECTED = "rejected",
	SELF = "self",
	SHARED_BY_VENDOR = "shared_by_vendor",
	SHARED_BY_PARTNER = "shared_by_partner"
}

export interface OpportunityCount {
	[key: string]: number;
}

export enum OpportunityType {
	ApplicationDevelopment = "application_development",
	Cabling = "cabling",
	ManagedService = "managed_service",
	Telephone = "telephone",
	Training = "training",
	VoIPSystem = "voip_system",
	HardwareRefresh = "hardware_refresh",
	HardwareSales = "hardware_sales",
	SoftwareSales = "software_sales",
	SoftwareRenewal = "software_renewal",
	ComplianceServices = "compliance_services",
	ContractRenewals = "contract_renewals",
	UserSupport = "user_support",
	RemoteSupport = "remote_support",
	Warranty = "warranty",
	ConsultingService = "consulting_service"
}

export const opportunityTypeOptions: Record<OpportunityType, string> = {
	[OpportunityType.ApplicationDevelopment]: "Application Development",
	[OpportunityType.Cabling]: "Cabling",
	[OpportunityType.ManagedService]: "Managed Service",
	[OpportunityType.Telephone]: "Telephone",
	[OpportunityType.Training]: "Training",
	[OpportunityType.VoIPSystem]: "VoIP System",
	[OpportunityType.HardwareRefresh]: "Hardware Refresh",
	[OpportunityType.HardwareSales]: "Hardware Sales",
	[OpportunityType.SoftwareSales]: "Software Sales",
	[OpportunityType.SoftwareRenewal]: "Software Renewal",
	[OpportunityType.ComplianceServices]: "Compliance Services",
	[OpportunityType.ContractRenewals]: "Contract Renewals",
	[OpportunityType.UserSupport]: "User Support",
	[OpportunityType.RemoteSupport]: "Remote Support",
	[OpportunityType.Warranty]: "Warranty",
	[OpportunityType.ConsultingService]: "Consulting Service"
};

export enum OpportunityRecentActivityTypeEnum {
	COMMENT,
	EMAIL
}

export class OpportunityRecentActivity {
	id = "";
	created: Date = new Date();
	updated: Date = new Date();
	type: OpportunityRecentActivityTypeEnum | null = null;
	when: Date = new Date();
	message = "";
}

export enum SalesActivitiesActionStatus {
	ActionOverdue = "action_overdue",
	ActionRequired = "action_required",
	UpcomingActionPresent = "upcoming_action_present"
}

export class OpportunityCostAndRevenueData {
	monthly = 0;
	annual = 0;
	grand = 0;
	one_time = 0;
	quarterly = 0;
	semi_annually = 0;
	weekly = 0;

	one_time_cost?: number = 0;
	weekly_cost?: number = 0;
	monthly_cost?: number = 0;
	quarterly_cost?: number = 0;
	semi_annually_cost?: number = 0;
	annual_cost?: number = 0;
	grand_cost?: number = 0;

	total_cost?: number = 0;
	total_revenue?: number = 0;

	margin_percentage?: number = 0;
	margin_value?: number = 0;
}

export enum OpportunitySharingStatus {
	SHARED = "shared",
	ACCEPTED = "accepted",
	REJECTED = "rejected"
}

export type OpportunityVendorCustomField = Pick<CustomField, "id" | "values">;

export interface OpportunityVendorCustomFields {
	vendor_opportunity_fields: OpportunityVendorCustomField[];
	vendor_client_company_fields: OpportunityVendorCustomField[];
	vendor_client_user_fields: {
		client_user_id: string;
		custom_fields: OpportunityVendorCustomField[];
	}[];
}

export interface OpportunityVendorDetails {
	vendor_company_id: string;
	vendor_opportunity_source: OpportunitySource;
	should_share_client_with_vendor: boolean;
	vendor_custom_fields?: OpportunityVendorCustomFields;
	status: OpportunitySharingStatus;
	shared_at: Date | null;
	accepted_at: Date | null;
	reject_reason?: string;
	vendor_opportunity_insights?: OpportunityInsights;
}

export interface Opportunity extends ArchievedWithSyncModel {
	activities?: { data: Activity[] };
	campaign: string | null;
	client_company_id: string;
	client_company_name: string | null;
	client_po_number: string | null;
	client_company_owner_id: string | null;
	close_probability: number | null;
	company_id: string;
	created: Date;
	display_value: number | null;
	documents?: { data: Document[] };
	estimated_close_date: Date | null;
	close_date: Date | null;
	estimated_value: number | null;
	forecasted_value?: number | null;
	pipeline_stage: OpportunityStage | null;
	id: string;
	linked_client_user_ids: string[];
	linked_client_users: ClientContact[];
	loss_reason: string | null;
	won_reason: string | null;
	name: string;
	opportunity_integration_data?: OpportunityIntegrationData;
	opportunity_pipeline_id: string;
	opportunity_pipeline_stage_id: string;
	opportunity_stage: string;
	opportunity_type?: OpportunityType | null;
	owner_user_id: string;
	owner_user: UserData;
	opportunity_vendor_details?: OpportunityVendorDetails | null;
	primary_client_user_id: string | null;
	primary_client_user: ClientContact | null;
	recent_activity_date: Date;
	recent_activity_list: OpportunityRecentActivity[];
	recent_activity: string;
	sales_activities_action_status?: SalesActivitiesActionStatus | null;
	source: string | null;
	total?: OpportunityCostAndRevenueData;
	updated: Date;
	custom_fields?: CustomField[];
	commission?: number;
	read_only?: boolean;
	client_sync_data?: SyncData[];
	rotten_time?: OpportunityRottenTime | null;
	manual_total?: OpportunityCostAndRevenueData;
	months_to_calculate_margin?: number;
	use_document_totals?: boolean;
	vendor?: VendorCompany;
	is_onboarding_entity?: boolean;
	currency: Currency | null;
}

export interface OpportunityRottenTime {
	rotten_days: number | null;
	rotten_hours: number | null;
}

export class AutotaskOpportunityIntegrationData {
	recognize_revenue_type: string | null = null;
	recognizeRevenuePeriodType: string | null = null;
	recognizeRevenuePeriod: number | null = null;
	shouldSync: boolean | null = null;
}

export class ConnectWiseOpportunityIntegrationData {
	business_unit: string | null = null;
	shouldSync: boolean | null = null;
}

export class OpportunityIntegrationData {
	autotaskOpportunityIntegrationData: AutotaskOpportunityIntegrationData | null =
		null;
	connectWiseOpportunityIntegrationData: ConnectWiseOpportunityIntegrationData | null =
		null;
}

export class OpportunityRequest {
	filters: TableQueryFilter[] = new Array<TableQueryFilter>();
	keywords: string | null = null;
	included_child_entities: ChildEntityHydrationParams[] =
		new Array<ChildEntityHydrationParams>();
	limit = 0;
	selected_id?: string;
	use_all_pipelines_flag?: boolean;
	constructor(
		filters?: TableQueryFilter[],
		keywords?: string | null,
		included_child_entities?: ChildEntityHydrationParams[],
		limit?: number,
		selected_id?: string,
		use_all_pipelines_flag?: boolean
	) {
		this.filters = filters || [];
		this.keywords = keywords || "";
		this.included_child_entities = included_child_entities || [];
		this.limit = limit || 100;
		this.selected_id = selected_id;
		this.use_all_pipelines_flag = use_all_pipelines_flag;
	}
}

export class OpportunityResponse {
	data: Opportunity[] = new Array<Opportunity>();
	filtered_count_map: OpportunityCount = {};
}

export type OpportunityViewKeys =
	| "client_company_name"
	| "company_id"
	| "display_value"
	| "linked_client_users"
	| "opportunity_stage"
	| "primary_client_user"
	| "recent_activity_date"
	| "owner_user"
	| "sales_activities_action_status"
	| "recent_activity_list"
	| "recent_activity"
	| "custom_fields";

export interface OpportunityFormValues
	extends Omit<
		Opportunity,
		"id" | "created" | "updated" | OpportunityViewKeys
	> {
	readonly custom_field_form_values: Record<string, string | string[]>;
	readonly share_opportunity_with_vendors?: boolean;
	readonly vendor_company_id?: string;
	readonly should_share_client_with_vendor?: boolean;
}

export interface AddOpportunityFormValues
	extends Omit<OpportunityFormValues, "close_date"> {
	readonly custom_field_form_values: Record<string, string | string[]>;
	readonly mandatory_custom_field_check_require: boolean;
	readonly is_onboarding_entity?: boolean;
}

export interface GetSyncErrorsInterface {
	readonly entity_type: string;
	readonly entity_id: string;
	readonly sync_source: string;
}

export class AddOpportunityRequest
	implements
		Omit<
			WithQueryFilter<AddOpportunityFormValues>,
			| "custom_field_form_values"
			| "sync_source"
			| "share_opportunity_with_vendors"
			| "vendor_company_id"
			| "should_share_client_with_vendor"
		>
{
	name: string;
	client_company_id: string;
	opportunity_type?: OpportunityType | null;
	primary_client_user_id: string | null;
	linked_client_user_ids: string[];
	estimated_close_date: Date | null;
	client_po_number: string | null;
	client_company_owner_id: string | null = null;
	close_probability: number | null;
	included_child_entities: ChildEntityHydrationParams[] = [];
	estimated_value: number | null;
	pipeline_stage: OpportunityStage | null = null;
	opportunity_pipeline_stage_id: string;
	opportunity_pipeline_id: string;
	owner_user_id: string;
	loss_reason: string | null = null;
	won_reason: string | null = null;
	campaign: string | null = null;
	source: string | null = null;
	custom_fields?: CustomField[];
	mandatory_custom_field_check_require = false;
	manual_total?: OpportunityCostAndRevenueData;
	use_document_totals?: boolean;
	months_to_calculate_margin?: number;
	opportunity_vendor_details?: OpportunityVendorDetails | null;
	is_onboarding_entity?: boolean;
	currency: Currency | null = null;

	constructor(args: WithQueryFilter<AddOpportunityFormValues>) {
		this.name = args.name;
		this.client_company_id = args.client_company_id;
		this.opportunity_type = args.opportunity_type;
		this.primary_client_user_id = args.primary_client_user_id;
		this.linked_client_user_ids =
			args.linked_client_user_ids?.filter(Boolean) || [];
		this.estimated_close_date = args.estimated_close_date;
		this.client_po_number = args.client_po_number;
		this.close_probability = args.close_probability;
		this.included_child_entities =
			args.included_child_entities ||
			new Array<ChildEntityHydrationParams>();
		this.estimated_value = args.estimated_value;
		this.owner_user_id = args.owner_user_id;
		this.opportunity_pipeline_stage_id = args.opportunity_pipeline_stage_id;
		this.opportunity_pipeline_id = args.opportunity_pipeline_id;
		this.loss_reason = args.loss_reason;
		this.won_reason = args.won_reason;
		this.campaign = args.campaign;
		this.source = args.source;
		this.mandatory_custom_field_check_require =
			args.mandatory_custom_field_check_require;
		this.manual_total = args.manual_total;
		this.months_to_calculate_margin = args.months_to_calculate_margin;
		this.use_document_totals = args.use_document_totals;
		this.opportunity_vendor_details =
			!!args.share_opportunity_with_vendors && args.vendor_company_id
				? {
						vendor_company_id: args.vendor_company_id,
						should_share_client_with_vendor: true,
						vendor_opportunity_source: OpportunitySource.PARTNER,
						status: OpportunitySharingStatus.SHARED,
						shared_at:
							args.opportunity_vendor_details?.shared_at ?? null,
						accepted_at:
							args.opportunity_vendor_details?.accepted_at ?? null
				  }
				: undefined;
		this.is_onboarding_entity = args.is_onboarding_entity;
		this.currency = args.currency ?? null;
	}
}

export interface EditOpportunityFormValues
	extends Omit<OpportunityFormValues, "close_date"> {
	readonly id: string;
	readonly custom_field_form_values: Record<string, string | string[]>;
	readonly mandatory_custom_field_check_require: boolean;
}

export class EditOpportunityRequest
	implements
		Omit<
			EditOpportunityFormValues,
			| "custom_field_form_values"
			| "sync_source"
			| "share_opportunity_with_vendors"
			| "vendor_company_id"
			| "should_share_client_with_vendor"
		>
{
	id: string;
	name: string;
	opportunity_type?: OpportunityType | null;
	primary_client_user_id: string | null;
	linked_client_user_ids: string[];
	estimated_close_date: Date | null = new Date();
	client_po_number: string | null = null;
	client_company_owner_id: string | null = null;
	close_probability: number | null = null;
	included_child_entities: ChildEntityHydrationParams[];
	estimated_value: number | null = null;
	pipeline_stage: OpportunityStage | null = null;
	opportunity_pipeline_stage_id: string;
	opportunity_pipeline_id: string;
	owner_user_id: string;
	loss_reason: string | null = null;
	won_reason: string | null = null;
	campaign: string | null = null;
	client_company_id: string;
	source: string | null = null;
	custom_fields?: CustomField[];
	mandatory_custom_field_check_require = false;
	manual_total?: OpportunityCostAndRevenueData;
	use_document_totals?: boolean;
	months_to_calculate_margin?: number;
	opportunity_vendor_details?: OpportunityVendorDetails | null;
	currency: Currency | null = null;

	constructor(args: WithQueryFilter<EditOpportunityFormValues>) {
		if (!args.id) {
			throw new Error("Invalid request: id is not available");
		}
		this.id = args.id;
		this.name = args.name;
		this.opportunity_type = args.opportunity_type;
		this.primary_client_user_id = args.primary_client_user_id;
		this.linked_client_user_ids =
			args.linked_client_user_ids || new Array<string>();
		this.estimated_close_date = args.estimated_close_date;
		this.client_po_number = args.client_po_number;
		this.close_probability = args.close_probability;
		this.included_child_entities =
			args.included_child_entities ||
			new Array<ChildEntityHydrationParams>();
		this.estimated_value = args.estimated_value;
		this.opportunity_pipeline_stage_id = args.opportunity_pipeline_stage_id;
		this.opportunity_pipeline_id = args.opportunity_pipeline_id;
		this.owner_user_id = args.owner_user_id;
		this.loss_reason = args.loss_reason;
		this.won_reason = args.won_reason;
		this.campaign = args.campaign;
		this.source = args.source;
		this.client_company_id = args.client_company_id;
		this.mandatory_custom_field_check_require =
			args.mandatory_custom_field_check_require;
		this.manual_total = args.manual_total;
		this.months_to_calculate_margin = args.months_to_calculate_margin;
		this.use_document_totals = args.use_document_totals;
		this.opportunity_vendor_details = args.opportunity_vendor_details;
		this.currency = args.currency ?? null;
	}
}

export interface ShareOpportunityWithVendorRequest {
	vendor_company_id: string | null;
	vendor_custom_fields: OpportunityVendorCustomFields | null;
	client_system_fields: {
		client_company_id: string;
		client_company_system_fields: Partial<Client>;
	} | null;
	client_contact_system_fields:
		| {
				client_contact_id: string;
				client_contact_system_fields: Partial<ClientContact>;
		  }[]
		| null;
}

export enum OpportunityView {
	List = "LIST",
	Kanban = "KANBAN"
}

export interface FetchOpportunityForStageIdRequest {
	stageId: string;
	pageNumber: number;
	pipelineId: string;
	activeUsers: string[];
}

export interface PipelineOpportunities extends OpportunityStage {
	readonly opportunities: Opportunity[];
}

export interface KanbanOpportunityResponse extends Pipeline {
	opportunity_stages: PipelineOpportunities[];
}

export interface KanbanOpportunityCountResponse {
	opportunity_stage_id: string;
	opportunity_count: number;
	opportunities_estimated_value: number;
}
// export interface KanbanOpportunityCountResponse extends Pipeline {
// 	data: StageIdCountPair[];
// }

export enum OpportunityFilters {
	MyOpportunities = "my_opportunities",
	AllOpportunities = "all_opportunities",
	NewThisWeek = "new_this_week",
	RecentlyUpdated = "recently_updated",
	OtherUsers = "selected_users",
	ClosingThisMonth = "closing_this_month",
	ClosingNextMonth = "closing_next_month",
	ClosedOpportunities = "closed_opportunities"
}

export interface CurrentActiveFilters {
	readonly opportunityFilter: OpportunityFilters;
	readonly userOpportunityFilter: string[];
	readonly shouldShowWonAndLost: boolean;
	readonly pipelineFilter?: string;
}

export enum OpportunityRottingLoadingState {
	notLoading = 0,
	win = 1,
	lost = -1
}

export interface BulkUpdateOpportunity {
	campaign?: string | null;
	client_company_id?: string;
	client_po_number?: string | null;
	close_probability?: number | null;
	estimated_close_date?: Date | null;
	estimated_value?: number | null;
	linked_client_user_ids?: string[];
	manual_total?: OpportunityCostAndRevenueData;
	months_to_calculate_margin?: number;
	opportunity_pipeline_stage_id?: string;
	opportunity_type?: OpportunityType | null;
	owner_user_id?: string;
	primary_client_user_id?: string | null;
	source?: string | null;
	use_document_totals?: boolean;
}
export interface OpportunityValue {
	fields: string;
	cost: string;
	revenue: string;
}
export interface CostAndRevenueData {
	onetime: number;
	weekly: number;
	monthly: number;
	quarterly: number;
	semi_annually: number;
	annual: number;
}

export interface ErrorMatchingEntities {
	id: string;
	name: string;
}
export interface SyncErrorAdditionalInfo {
	entity_id: string;
	entity_type:
		| ZomentumEntities.Client
		| ZomentumEntities.Opportunity
		| ZomentumEntities.ClientContact;
	entity_name: string;
	description?: string;
	matching_entities?: ErrorMatchingEntities[];
	missing_permissions?: string[];
	errors?: string[];
}

export interface SyncError {
	sync_error_type: ZomentumPSAErrorTypes;
	sync_error_additional_info: SyncErrorAdditionalInfo[];
}

export interface allSyncErrors {
	sync_source: string;
	multiple_errors: boolean;
	sync_errors: SyncError[];
}

export interface OpportunityInsights {
	updated: Date;
	stage?: string;
	stage_index?: number;
	no_of_stages?: number;
	is_terminal_win_state?: boolean;
	is_terminal_lost_state?: boolean;
}

export interface PendingOpportunitySharedWithPartner {
	id: string;
	created: Date;
	updated: Date;
	name: string;
	vendor_id: string;
	vendor?: VendorCompany | null;
	vendor_client_company?: Pick<
		VendorClient,
		| "id"
		| "name"
		| "billing_address"
		| "shipping_addresses"
		| "fax"
		| "website"
		| "linked_in_url"
		| "facebook_url"
		| "twitter_url"
		| "company_type"
		| "company_types"
	>;
	vendor_opportunity_type?: OpportunityType | null;
	opportunity_stage?: string;
	estimated_close_date?: Date;
	estimated_value?: number;
	opportunity_pipeline_id: string;
	opportunity_pipeline_stage_id: string;
	shared_at?: Date;
	existing_client_company_id?: string;
	duplicate_opportunity_id?: string;
}

export class PendingOpportunitySharedWithPartnerResponse {
	data: PendingOpportunitySharedWithPartner[] =
		new Array<PendingOpportunitySharedWithPartner>();
}

export interface AcceptPendingOpportunitySharedWithPartnerRequest {
	client_company_id?: string;
}

export interface RejectPendingOpportunitySharedWithPartnerRequest {
	reject_reason: string;
}

export interface RecentOpportunityItemView {
	readonly id: string;
	readonly name: string;
	readonly client_company_id?: string | null;
	readonly primary_client_user_id?: string | null;
}

export interface RecentOpportunityRequest {
	readonly client_company_ids?: string[];
	readonly client_user_ids?: string[];
}

export interface RecentOpportunityResponse {
	readonly data: RecentOpportunityItemView[];
	readonly filtered_count_map?: Record<string, number> | null;
	readonly errors?: string[] | null;
}
